import { Link } from 'react-router-dom';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import { CONTACT_EMAIL } from '@/data/site';
import { TOTAL_FILMS, TOTAL_MOVIES, TOTAL_SERIES } from '@/data/catalog';
import Breadcrumb from '@/components/feature/Breadcrumb';

const SCOPE_ITEMS = [
  {
    icon: 'ri-film-line',
    title: '最新电影',
    text: '收录近期上线的剧情、科幻、悬疑、动画等类型电影，标注年份、地区与片长。',
  },
  {
    icon: 'ri-tv-2-line',
    title: '热门剧集',
    text: '覆盖国产剧、欧美剧、日韩剧，提供集数、主演与剧情概述，方便判断是否值得追。',
  },
  {
    icon: 'ri-bookmark-3-line',
    title: '主题专题',
    text: '围绕类型与情绪制作的片单合集，把同一类作品放在一起，减少选择成本。',
  },
  {
    icon: 'ri-user-star-line',
    title: '演职资料',
    text: '每部作品都附导演与主演信息，可作为观影前后的快速参考资料。',
  },
];

const NOTICES = [
  '站内所有影视条目、评分与简介均为演示数据，不对应真实作品。',
  '本站不提供在线播放、下载或任何形式的资源分发。',
  '分类筛选、搜索、分页与分享按钮均在前端完成模拟，不会向服务器提交数据。',
  '如需更正演示内容或洽谈内容合作，可通过下方邮箱与我们联系。',
];

export default function About() {
  useDocumentMeta({
    title: '关于我们 - 大雷影视大全',
    description:
      '大雷影视是面向影视爱好者的内容发现平台，提供最新电影、电视剧的分类浏览、资料查询与主题片单，帮助观众更快找到想看的作品。',
    keywords: '大雷影视,关于我们,影视平台介绍,影视资讯,电影推荐',
  });

  return (
    <div>
      <section className="w-full bg-primary-950 py-10 md:py-14">
        <div className="mx-auto w-full max-w-[1440px] px-4 md:px-8">
          <Breadcrumb items={[{ label: '关于我们' }]} light />
          <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-12 lg:items-end">
            <div className="lg:col-span-7">
              <p className="font-label text-xs uppercase tracking-[0.32em] text-accent-400">About Us</p>
              <h1 className="mt-3 text-3xl font-black leading-tight tracking-tight text-background-50 md:text-5xl">
                关于大雷影视
              </h1>
              <p className="mt-4 max-w-2xl text-sm leading-7 text-background-200/85 md:text-[15px]">
                大雷影视站聚焦影视内容的发现与观看辅助。我们相信，观众缺的从来不是片源，而是一个能快速判断「这部值不值得看」的地方。所以我们把资料、分类和片单整理清楚，把选择权交回给你。
              </p>
            </div>
            <div className="grid grid-cols-3 gap-3 lg:col-span-5">
              {[
                { value: `${TOTAL_MOVIES}`, label: '演示条目' },
                { value: `${TOTAL_FILMS}`, label: '电影' },
                { value: `${TOTAL_SERIES}`, label: '剧集' },
              ].map((stat) => (
                <div key={stat.label} className="rounded-lg border border-background-50/15 bg-background-50/5 px-4 py-5">
                  <p className="font-label text-3xl font-semibold leading-none text-accent-400">{stat.value}</p>
                  <p className="mt-2 text-xs text-background-300/80">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <div className="mx-auto w-full max-w-[1440px] px-4 py-12 md:px-8 md:py-16">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-14">
          <div className="lg:col-span-7">
            <section>
              <div className="flex items-center gap-2.5">
                <span className="h-5 w-1 rounded-full bg-accent-500" />
                <h2 className="text-xl font-bold tracking-tight text-foreground-950 md:text-2xl">网站定位</h2>
              </div>
              <p className="mt-4 text-sm leading-8 text-foreground-600 md:text-[15px]">
                大雷影视是一个影视内容发现与观看辅助平台，面向广大影视爱好者和普通观众。我们不生产内容，也不做播放，只做一件事：把正在上映、正在更新和值得回看的作品整理成容易浏览的结构，让查找一部片子的时间从十几分钟缩短到一分钟。
              </p>
              <p className="mt-4 text-sm leading-8 text-foreground-600 md:text-[15px]">
                站内以电影与电视剧两条主线组织内容，辅以地区、年份、类型与热度四个维度。首页负责推荐，分类页负责筛选，详情页负责判断，专题页负责把相似的作品串起来。任何一个页面都能直接跳到另外三个页面。
              </p>
            </section>

            <section className="mt-12">
              <div className="flex items-center gap-2.5">
                <span className="h-5 w-1 rounded-full bg-accent-500" />
                <h2 className="text-xl font-bold tracking-tight text-foreground-950 md:text-2xl">内容范围</h2>
              </div>
              <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
                {SCOPE_ITEMS.map((item) => (
                  <div key={item.title} className="rounded-lg border border-background-200 bg-background-50 p-5">
                    <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-50 text-primary-600">
                      <i className={`${item.icon} text-lg`} />
                    </span>
                    <h3 className="mt-3 text-sm font-medium text-foreground-950">{item.title}</h3>
                    <p className="mt-1.5 text-xs leading-relaxed text-foreground-500">{item.text}</p>
                  </div>
                ))}
              </div>
            </section>

            <section className="mt-12">
              <div className="flex items-center gap-2.5">
                <span className="h-5 w-1 rounded-full bg-accent-500" />
                <h2 className="text-xl font-bold tracking-tight text-foreground-950 md:text-2xl">使用须知</h2>
              </div>
              <ul className="mt-5 space-y-3">
                {NOTICES.map((notice, index) => (
                  <li key={notice} className="flex gap-3 rounded-lg bg-background-100 px-4 py-3.5">
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-background-50 font-label text-xs text-foreground-500">
                      {index + 1}
                    </span>
                    <p className="text-sm leading-relaxed text-foreground-600">{notice}</p>
                  </li>
                ))}
              </ul>
            </section>
          </div>

          <aside className="lg:col-span-5">
            <div className="rounded-lg border border-background-200 bg-background-50 p-6">
              <h2 className="flex items-center gap-2 text-base font-semibold text-foreground-950">
                <i className="ri-mail-send-line text-accent-500" />
                联系我们
              </h2>
              <p className="mt-2 text-xs leading-relaxed text-foreground-500">
                内容纠错、合作洽谈或产品建议，都可以直接发邮件给我们，我们会在工作日 48 小时内回复。
              </p>

              <a
                href={`mailto:${CONTACT_EMAIL}`}
                className="mt-5 flex cursor-pointer items-center gap-3 rounded-md bg-primary-50 px-4 py-3.5 transition-colors hover:bg-primary-100"
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-background-50 text-primary-600">
                  <i className="ri-mail-line text-lg" />
                </span>
                <span className="min-w-0">
                  <span className="block text-[11px] text-primary-700/70">官方邮箱</span>
                  <span className="block truncate text-sm font-medium text-primary-800">{CONTACT_EMAIL}</span>
                </span>
              </a>

              <div className="mt-4 grid grid-cols-1 gap-2">
                {[
                  { icon: 'ri-weibo-fill', label: '微博', handle: '@大雷影视大全' },
                  { icon: 'ri-wechat-fill', label: '微信公众号', handle: '大雷影视' },
                  { icon: 'ri-bilibili-fill', label: '哔哩哔哩', handle: '大雷影视官方' },
                ].map((item) => (
                  <a
                    key={item.label}
                    href="https://www.example.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex cursor-pointer items-center gap-3 rounded-md border border-background-200 px-3.5 py-3 transition-colors hover:border-primary-300"
                  >
                    <i className={`${item.icon} text-base text-foreground-500`} />
                    <span className="flex-1 text-sm text-foreground-700">{item.label}</span>
                    <span className="text-xs text-foreground-400">{item.handle}</span>
                    <i className="ri-arrow-right-up-line text-xs text-foreground-300" />
                  </a>
                ))}
              </div>

              <p className="mt-4 text-[11px] leading-relaxed text-foreground-400">
                以上为演示联系信息，外部链接仅做跳转示意。
              </p>
            </div>

            <div className="mt-4 rounded-lg bg-secondary-100 p-6">
              <h2 className="flex items-center gap-2 text-base font-semibold text-secondary-900">
                <i className="ri-compass-3-line text-secondary-700" />
                从哪里开始
              </h2>
              <div className="mt-3 flex flex-col gap-2">
                <Link
                  to="/category/movie"
                  className="flex cursor-pointer items-center justify-between rounded-md bg-background-50 px-3.5 py-2.5 text-sm text-foreground-700 transition-colors hover:text-primary-600"
                >
                  浏览电影分类
                  <i className="ri-arrow-right-s-line" />
                </Link>
                <Link
                  to="/special/special-03"
                  className="flex cursor-pointer items-center justify-between rounded-md bg-background-50 px-3.5 py-2.5 text-sm text-foreground-700 transition-colors hover:text-primary-600"
                >
                  看治愈系片单
                  <i className="ri-arrow-right-s-line" />
                </Link>
                <Link
                  to="/search"
                  className="flex cursor-pointer items-center justify-between rounded-md bg-background-50 px-3.5 py-2.5 text-sm text-foreground-700 transition-colors hover:text-primary-600"
                >
                  直接搜索作品
                  <i className="ri-arrow-right-s-line" />
                </Link>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}